import React, { useState } from 'react'

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Tooltip, Typography } from '@mui/material'

import { ConfirmModal } from 'components/_base'
import { FormAutoComplete, FormTextField } from 'components/input'
import { getCountryMap } from 'utils/countries'
import { getLinkedInIndustryMap } from 'utils/industries'
import { getNaceMap } from 'utils/nace'

const countries = [...getCountryMap()].map(([iso, country]: any) => ({ value: iso, label: country.printableName }))
const naces = [...getNaceMap()].map(([code, nace]: any) => ({ value: code, label: `${nace.code} ${nace.name}` }))

// Export streams every matching company (the service ignores limit/offset), so confirm above this.
const CONFIRM_THRESHOLD = 10000

type CompaniesFiltersModalProps = {
  count?: number
  open: boolean
  onClose: (...args: any[]) => any
  onExport: (...args: any[]) => any
  onReset: (...args: any[]) => any
}

const CompaniesFiltersModal = ({ count, onExport, open, onClose, onReset }: CompaniesFiltersModalProps) => {
  const linkedinIndustries = [...getLinkedInIndustryMap()]
    .sort(([, a]: any, [, b]: any) => a.code - b.code)
    .map(([code, industry]: any) => ({ value: code, label: `${industry.code} ${industry.category}` }))

  const [confirmFormat, setConfirmFormat] = useState<string | null>(null)

  const countKnown = count != null
  const canExport = countKnown && count > 0

  const countLabel = !countKnown
    ? 'Counting…'
    : `${count.toLocaleString()} ${count === 1 ? 'company matches' : 'companies match'}`
  const disabledReason = countKnown ? 'No companies match the current filters' : 'Loading count…'

  const handleExportClick = (format: string) => () => {
    if (count != null && count > CONFIRM_THRESHOLD) setConfirmFormat(format)
    else onExport(format)()
  }

  return (
    <Dialog onClose={onClose} open={open}>
      <DialogTitle>Filter</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ width: 550 }}>
          <Grid
            size={{
              sm: 4,
            }}>
            <FormTextField label="Search" name="search" />
          </Grid>
          <Grid
            size={{
              sm: 4,
            }}>
            <FormTextField label="Website domain" name="url" />
          </Grid>
          <Grid
            size={{
              sm: 4,
            }}>
            <FormTextField label="Wildcards" name="wildcards" />
          </Grid>

          <Grid
            size={{
              sm: 12,
            }}>
            <FormAutoComplete label="Nace codes" name="naces" options={naces} />
          </Grid>

          <Grid
            size={{
              sm: 12,
            }}>
            <FormAutoComplete label="Linkedin industries" name="industries" options={linkedinIndustries} />
          </Grid>

          <Grid
            size={{
              sm: 12,
            }}>
            <FormAutoComplete label="Countries" name="countries" options={countries} />
          </Grid>

          <Grid
            size={{
              sm: 3,
            }}>
            <FormTextField label="Min revenue" name="revenue_min" type="number" />
          </Grid>
          <Grid
            size={{
              sm: 3,
            }}>
            <FormTextField label="Max revenue" name="revenue_max" type="number" />
          </Grid>

          <Grid
            size={{
              sm: 3,
            }}>
            <FormTextField label="Min employees" name="employees_min" type="number" />
          </Grid>
          <Grid
            size={{
              sm: 3,
            }}>
            <FormTextField label="Max employees" name="employees_max" type="number" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Typography color="text.secondary" variant="caption">
          {countLabel}
        </Typography>
        <span>
          <Button onClick={onReset}>Clear</Button>
          <Tooltip title={canExport ? '' : disabledReason}>
            <span>
              <Button disabled={!canExport} onClick={handleExportClick('csv')}>
                Export
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={canExport ? '' : disabledReason}>
            <span>
              <Button disabled={!canExport} onClick={handleExportClick('extended_csv')}>
                Export extended
              </Button>
            </span>
          </Tooltip>
        </span>
      </DialogActions>
      <ConfirmModal
        content={
          count != null ? `This will export ${count.toLocaleString()} companies and may be slow or time out.` : ''
        }
        onClose={() => setConfirmFormat(null)}
        onConfirm={() => {
          if (confirmFormat) onExport(confirmFormat)()
          setConfirmFormat(null)
        }}
        open={confirmFormat !== null}
        title="Export a large result set?"
      />
    </Dialog>
  )
}

export default CompaniesFiltersModal
